import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  NewplaAiChatApi, NewplaAiChatMessageDto, NewplaAiChatOptionsDto, NewplaAiChatRoomListDto, NewplaAiModel,
  NewplaAiRole
} from './newpla-ai-chat.api';
import { renderNewplaAiMarkdown } from './newpla-ai-markdown';
import { NewplaAiXhrError, NewplaAiXhrProgress } from './newpla-ai-xhr';
import { NewplaAiUserSettingComponent } from '../newpla-ai-user-setting/newpla-ai-user-setting.component';

/** 첨부한 파일 (보내기 전). 브라우저 안에만 있다 */
interface PendingFileVm {
  key: string;
  file: File;
  kind: 'image' | 'pdf' | 'text';
  /** 이미지 미리보기 주소 (blob:). 빼거나 보내면 바로 해제한다 */
  previewUrl?: string;
}

/** 화면에 보이는 메시지. 보내는 중인 질문과 실패한 질문도 같이 보인다 */
interface ChatMessageVm {
  key: string;
  role: NewplaAiRole;
  content: string;
  html?: SafeHtml;
  model?: string | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  createdAt?: string;
  failed?: boolean;
  /** 이 질문에 붙인 파일 이름. 파일은 저장하지 않아서 방을 다시 열면 보이지 않는다 */
  fileNames?: string[];
  /** 다시 보내기용. 실패한 질문에만 남긴다 */
  retryFiles?: File[];
}

interface RoomGroupVm {
  label: string;
  rooms: NewplaAiChatRoomListDto[];
}

/** 이름(확장자)으로 본 종류. 서버가 내용으로 다시 확인한다 */
const TYPE_BY_EXTENSION: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif',
  pdf: 'application/pdf', txt: 'text/plain', csv: 'text/csv', md: 'text/markdown', markdown: 'text/markdown',
};

/** 파일과 AI 처리를 합쳐 기다리는 최대 시간 */
const SEND_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * AI 채팅 (ChatGPT·Ollama 웹 같은 화면)
 * - 왼쪽: 새 채팅, 검색, 날짜별 채팅방 목록(이름 바꾸기·삭제)
 * - 오른쪽: 대화(답은 마크다운), 입력창(Enter 보내기, Shift+Enter 줄바꿈, 한글 조합 중 Enter는 무시)
 * - 방은 첫 질문을 보낼 때 서버가 만든다
 * - 모델: 위쪽에서 고른다(서버가 고를 수 있게 했을 때). 방을 열면 그 방에서 마지막에 쓴 모델, 새 채팅은 마지막에 고른 모델
 * - 설정: 오른쪽 위 "설정"에서 개인 AI 설정(newpla-ai-user-setting, AI 공통)을 오른쪽 패널로 연다
 * - 첨부: 대화 영역에 파일을 끌어 놓는다. 개수·크기·종류를 먼저 보고, 서버가 내용으로 다시 확인한다.
 *   파일은 저장하지 않고 그 질문에만 AI로 간다. 보내는 동안 진행률을 보이고 취소할 수 있다
 *
 * - 페이지로도, NgbModal로도 연다. 모달이면 높이를 꽉 채우고 오른쪽 위에 닫기(X)가 생긴다
 *
 * 예: <newpla-ai-chat /> 또는 ngbModal.open(NewplaAiChatComponent, { modalDialogClass: 'newpla-ai-chat-modal-dialog' })
 */
@Component({
  selector: 'newpla-ai-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, NewplaAiUserSettingComponent],
  templateUrl: './newpla-ai-chat.component.html',
  styleUrl: './newpla-ai-chat.component.scss',
})
export class NewplaAiChatComponent implements OnInit, AfterViewInit, OnDestroy {

  private api = inject(NewplaAiChatApi);
  private sanitizer = inject(DomSanitizer);
  /** 모달로 열렸을 때만 있다 */
  readonly activeModal = inject(NgbActiveModal, { optional: true });

  @HostBinding('class.in-modal')
  get inModal(): boolean {
    return !!this.activeModal;
  }

  @ViewChild('scroller') scroller?: ElementRef<HTMLElement>;
  @ViewChild('input') input?: ElementRef<HTMLTextAreaElement>;

  /** 빈 화면의 추천 질문. 사내 지식(작업지시·작업관리·정산 매뉴얼)으로 답할 수 있는 것 */
  readonly suggestions = [
    '작업지시는 어떻게 만들고 완료해?',
    '홀딩한 작업을 다시 진행하려면 어떻게 해?',
    '부품수리 작업이 완료되지 않는 이유가 뭐야?',
    '작업 정산 전표는 어떻게 만들어?',
  ];

  rooms: NewplaAiChatRoomListDto[] = [];
  roomGroups: RoomGroupVm[] = [];
  roomFilter = '';
  roomsLoading = false;

  currentRoomId: string | null = null;
  currentTitle = '';
  messages: ChatMessageVm[] = [];
  roomLoading = false;

  draft = '';
  sending = false;
  errorText = '';
  sidebarOpen = true;

  options: NewplaAiChatOptionsDto = {
    modelSelectable: false, defaultModel: null, models: [],
    attachmentEnabled: false, attachmentMaxFileBytes: 0, attachmentMaxFiles: 0, attachmentTypes: [],
  };
  selectedModel: string | null = null;
  modelMenuOpen = false;

  pendingFiles: PendingFileVm[] = [];
  /** 끌어 놓을 때 막은 파일과 이유. 잠시 보이고 사라진다 */
  fileRejects: string[] = [];
  dragActive = false;
  progress: NewplaAiXhrProgress | null = null;

  settingOpen = false;

  renamingRoomId: string | null = null;
  renameText = '';
  copiedKey: string | null = null;

  formatBytes = formatBytes;

  private readonly modelSaveKey = 'newpla-ai-chat-model';
  private composing = false;
  private keySeq = 0;
  /** dragenter/leave는 자식 요소마다 오므로 깊이를 센다 */
  private dragDepth = 0;
  private sendAbort: AbortController | null = null;
  private rejectTimer?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.sidebarOpen = window.innerWidth >= 768;
    this.loadRooms();
    this.loadOptions();
  }

  ngAfterViewInit(): void {
    this.focusInput();
  }

  ngOnDestroy(): void {
    this.sendAbort?.abort();
    this.clearPendingFiles();
    clearTimeout(this.rejectTimer);
  }

  // ================================================================ 설정·모델

  async loadOptions(): Promise<void> {
    this.options = await this.api.options();
    this.selectedModel = this.availableModel(readSaved(this.modelSaveKey)) ?? this.options.defaultModel;
  }

  /** 답 아래에 보일 모델 이름. 목록에 있으면 화면 이름(예: newpla-ai), 없으면 id 그대로 */
  modelName(id: string | null | undefined): string {
    return this.options.models.find(m => m.id === id)?.name ?? id ?? '';
  }

  get selectedModelInfo(): NewplaAiModel | undefined {
    return this.options.models.find(m => m.id === this.selectedModel);
  }

  toggleModelMenu(event: Event): void {
    event.stopPropagation();
    this.modelMenuOpen = this.options.modelSelectable && !this.modelMenuOpen;
  }

  selectModel(model: NewplaAiModel): void {
    this.selectedModel = model.id;
    this.modelMenuOpen = false;
    writeSaved(this.modelSaveKey, model.id);
    this.focusInput();
  }

  closeMenus(): void {
    this.modelMenuOpen = false;
  }

  /** 고를 수 있는 모델이면 그대로, 아니면 null */
  private availableModel(id: string | null | undefined): string | null {
    return id && this.options.models.some(m => m.id === id) ? id : null;
  }

  // ================================================================ 채팅방 목록

  async loadRooms(): Promise<void> {
    this.roomsLoading = true;
    try {
      this.rooms = await this.api.findMyRooms();
      this.groupRooms();
    } finally {
      this.roomsLoading = false;
    }
  }

  groupRooms(): void {
    const filter = this.roomFilter.trim().toLowerCase();
    const list = filter ? this.rooms.filter(r => r.title.toLowerCase().includes(filter)) : this.rooms;
    const today = startOfDay(new Date());
    const day = 24 * 60 * 60 * 1000;
    const groups: RoomGroupVm[] = [
      { label: '오늘', rooms: [] },
      { label: '어제', rooms: [] },
      { label: '지난 7일', rooms: [] },
      { label: '지난 30일', rooms: [] },
      { label: '이전', rooms: [] },
    ];
    for (const room of list) {
      const t = startOfDay(new Date(room.lastMessageAt)).getTime();
      const diff = Math.round((today.getTime() - t) / day);
      const idx = diff <= 0 ? 0 : diff === 1 ? 1 : diff <= 7 ? 2 : diff <= 30 ? 3 : 4;
      groups[idx].rooms.push(room);
    }
    this.roomGroups = groups.filter(g => g.rooms.length > 0);
  }

  newChat(): void {
    if (this.sending) {
      return;
    }
    this.currentRoomId = null;
    this.currentTitle = '';
    this.messages = [];
    this.draft = '';
    this.clearPendingFiles();
    this.selectedModel = this.availableModel(readSaved(this.modelSaveKey)) ?? this.options.defaultModel;
    this.closeSidebarOnMobile();
    this.focusInput();
  }

  async openRoom(room: NewplaAiChatRoomListDto): Promise<void> {
    if (this.sending || this.renamingRoomId || room.id === this.currentRoomId) {
      return;
    }
    this.currentRoomId = room.id;
    this.currentTitle = room.title;
    this.messages = [];
    this.roomLoading = true;
    this.closeSidebarOnMobile();
    try {
      const detail = await this.api.getMyRoom(room.id);
      if (this.currentRoomId !== room.id) {
        return; // 읽는 사이 다른 방을 골랐다
      }
      this.currentTitle = detail.title;
      this.messages = detail.messages.map(m => this.toVm(m));
      this.selectedModel = this.availableModel(detail.model) ?? this.selectedModel;
      this.scrollToBottom(false);
      this.focusInput();
    } finally {
      this.roomLoading = false;
    }
  }

  startRename(room: NewplaAiChatRoomListDto, event: Event): void {
    event.stopPropagation();
    this.renamingRoomId = room.id;
    this.renameText = room.title;
    setTimeout(() => (document.querySelector('.np-ai-rename-input') as HTMLInputElement | null)?.select());
  }

  async commitRename(room: NewplaAiChatRoomListDto): Promise<void> {
    if (this.renamingRoomId !== room.id) {
      return;
    }
    const title = this.renameText.trim();
    this.renamingRoomId = null;
    if (!title || title === room.title) {
      return;
    }
    const saved = await this.api.rename(room.id, title);
    room.title = saved.title;
    if (this.currentRoomId === room.id) {
      this.currentTitle = saved.title;
    }
    this.groupRooms();
  }

  cancelRename(): void {
    this.renamingRoomId = null;
  }

  async deleteRoom(room: NewplaAiChatRoomListDto, event: Event): Promise<void> {
    event.stopPropagation();
    if (!confirm(`"${room.title}" 채팅을 삭제할까요?`)) {
      return;
    }
    await this.api.delete(room.id);
    this.rooms = this.rooms.filter(r => r.id !== room.id);
    this.groupRooms();
    if (this.currentRoomId === room.id) {
      this.newChat();
    }
  }

  // ================================================================ 첨부 (끌어 놓기)

  onDragEnter(event: DragEvent): void {
    if (!isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    this.dragDepth++;
    this.dragActive = true;
  }

  onDragOver(event: DragEvent): void {
    if (!isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.options.attachmentEnabled ? 'copy' : 'none';
    }
  }

  onDragLeave(event: DragEvent): void {
    if (!isFileDrag(event)) {
      return;
    }
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.dragActive = false;
    }
  }

  onDrop(event: DragEvent): void {
    if (!isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    this.dragDepth = 0;
    this.dragActive = false;
    if (!this.options.attachmentEnabled) {
      this.showRejects(['파일을 첨부할 수 없습니다.']);
      return;
    }
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  removeFile(pending: PendingFileVm): void {
    revoke(pending);
    this.pendingFiles = this.pendingFiles.filter(f => f !== pending);
  }

  get attachmentHint(): string {
    if (!this.options.attachmentEnabled) {
      return '';
    }
    return `파일은 끌어 놓으세요 · 이미지·PDF·글 · ${this.options.attachmentMaxFiles}개까지 · 개당 ${formatBytes(this.options.attachmentMaxFileBytes)}`;
  }

  /** 개수·크기·종류·중복을 보고 붙인다. 막은 파일은 이유를 보인다 */
  private addFiles(files: File[]): void {
    const rejects: string[] = [];
    const max = this.options.attachmentMaxFiles;
    for (const file of files) {
      const type = typeOf(file);
      if (this.pendingFiles.some(p => sameFile(p.file, file))) {
        continue; // 같은 파일을 두 번 놓았다
      }
      if (this.pendingFiles.length >= max) {
        rejects.push(`파일은 한 번에 ${max}개까지 보낼 수 있습니다: ${file.name}`);
        continue;
      }
      if (file.size === 0) {
        rejects.push(`빈 파일입니다: ${file.name}`);
        continue;
      }
      if (file.size > this.options.attachmentMaxFileBytes) {
        rejects.push(`${formatBytes(this.options.attachmentMaxFileBytes)}보다 큽니다: ${file.name} (${formatBytes(file.size)})`);
        continue;
      }
      if (!type || !this.options.attachmentTypes.includes(type)) {
        rejects.push(`보낼 수 없는 형식입니다: ${file.name}`);
        continue;
      }
      const kind = type.startsWith('image/') ? 'image' : type === 'application/pdf' ? 'pdf' : 'text';
      this.pendingFiles = [...this.pendingFiles, {
        key: this.nextKey(),
        file,
        kind,
        // HEIC는 브라우저 대부분이 그리지 못해 아이콘으로 보인다
        previewUrl: kind === 'image' && !/hei[cf]$/.test(type) ? URL.createObjectURL(file) : undefined,
      }];
    }
    this.showRejects(rejects);
    this.focusInput();
  }

  private showRejects(rejects: string[]): void {
    clearTimeout(this.rejectTimer);
    this.fileRejects = rejects;
    if (rejects.length) {
      this.rejectTimer = setTimeout(() => (this.fileRejects = []), 6000);
    }
  }

  private clearPendingFiles(): void {
    this.pendingFiles.forEach(revoke);
    this.pendingFiles = [];
  }

  // ================================================================ 보내기

  onKeydown(event: KeyboardEvent): void {
    // 한글 조합 중 Enter는 글자 확정이라 보내지 않는다
    if (event.key === 'Enter' && !event.shiftKey && !this.composing && !event.isComposing) {
      event.preventDefault();
      this.send();
    }
  }

  onCompositionStart(): void {
    this.composing = true;
  }

  onCompositionEnd(): void {
    this.composing = false;
  }

  autoGrow(): void {
    const el = this.input?.nativeElement;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 220) + 'px';
    }
  }

  useSuggestion(text: string): void {
    this.draft = text;
    this.send();
  }

  /**
   * @param retry 실패한 질문을 다시 보낼 때 그 글과 파일
   */
  async send(retry?: { text: string; files: File[] }): Promise<void> {
    const message = (retry?.text ?? this.draft).trim();
    if (!message || this.sending) {
      return;
    }
    const files = retry ? retry.files : this.pendingFiles.map(p => p.file);
    const roomId = this.currentRoomId;
    const model = this.options.modelSelectable ? this.selectedModel : null;
    const pending: ChatMessageVm = {
      key: this.nextKey(), role: 'USER', content: message, fileNames: files.map(f => f.name),
    };
    this.messages = [...this.messages.filter(m => !m.failed), pending];
    if (!retry) {
      this.draft = '';
      this.clearPendingFiles();
      setTimeout(() => this.autoGrow());
    }
    this.sending = true;
    this.errorText = '';
    this.progress = null;
    this.scrollToBottom(true);

    const abort = new AbortController();
    this.sendAbort = abort;
    try {
      const result = files.length === 0
        ? await this.api.send(roomId, message, model)
        : await this.api.sendWithFiles(roomId, message, model, files, {
          signal: abort.signal,
          timeoutMs: SEND_TIMEOUT_MS,
          onProgress: p => (this.progress = p),
        });
      if (this.currentRoomId !== roomId) {
        // 기다리는 사이 다른 방으로 옮겼다. 목록만 갱신한다
        this.upsertRoom(result.room);
        return;
      }
      this.currentRoomId = result.room.id;
      this.currentTitle = result.room.title;
      const question = this.toVm(result.question);
      question.fileNames = pending.fileNames;
      this.messages = [...this.messages.filter(m => m !== pending), question, this.toVm(result.answer)];
      this.upsertRoom(result.room);
      this.scrollToBottom(true);
    } catch (e) {
      pending.failed = true;
      pending.retryFiles = files;
      this.messages = [...this.messages];
      this.errorText = errorMessage(e);
      if (e instanceof NewplaAiXhrError && e.kind === 'abort') {
        // 서버가 이미 받았으면 저장됐을 수 있다. 목록을 다시 읽어 맞춘다
        this.loadRooms();
      }
      this.scrollToBottom(true);
    } finally {
      this.sending = false;
      this.progress = null;
      this.sendAbort = null;
      this.focusInput();
    }
  }

  /** 파일을 보내는 중일 때만 취소할 수 있다 (글만 보낼 때는 HttpClient라 진행률이 없다) */
  get canCancel(): boolean {
    return this.sending && this.progress !== null;
  }

  cancelSend(): void {
    this.sendAbort?.abort();
  }

  retry(message: ChatMessageVm): void {
    this.messages = this.messages.filter(m => m !== message);
    this.errorText = '';
    this.send({ text: message.content, files: message.retryFiles ?? [] });
  }

  // ================================================================ 복사

  async copy(message: ChatMessageVm): Promise<void> {
    await navigator.clipboard.writeText(message.content);
    this.copiedKey = message.key;
    setTimeout(() => (this.copiedKey === message.key ? (this.copiedKey = null) : null), 1500);
  }

  /** 답 안의 코드 블록 복사 버튼 (마크다운 HTML 안이라 클릭을 위임받는다) */
  async onAnswerClick(event: MouseEvent): Promise<void> {
    const btn = (event.target as HTMLElement).closest('.np-ai-code-copy');
    if (!btn) {
      return;
    }
    const code = btn.closest('.np-ai-code')?.querySelector('code')?.textContent ?? '';
    await navigator.clipboard.writeText(code);
    btn.innerHTML = '<i class="bi bi-check2"></i> 복사됨';
    setTimeout(() => (btn.innerHTML = '<i class="bi bi-clipboard"></i> 복사'), 1500);
  }

  // ================================================================ 설정 패널

  toggleSetting(event?: Event): void {
    event?.stopPropagation();
    this.settingOpen = !this.settingOpen;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.settingOpen = false;
    this.modelMenuOpen = false;
  }

  // ================================================================ 기타

  /** 모달 닫기. 보내는 중이면 묻는다(답을 기다리는 중에 닫으면 서버는 계속 처리해 저장한다) */
  closeModal(): void {
    if (this.sending && !confirm('답을 기다리는 중입니다. 닫을까요? (답은 채팅 목록에 저장됩니다)')) {
      return;
    }
    this.activeModal?.close();
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  trackByKey(_: number, m: { key: string }): string {
    return m.key;
  }

  trackById(_: number, r: NewplaAiChatRoomListDto): string {
    return r.id;
  }

  private upsertRoom(room: NewplaAiChatRoomListDto): void {
    this.rooms = [room, ...this.rooms.filter(r => r.id !== room.id)];
    this.groupRooms();
  }

  private toVm(m: NewplaAiChatMessageDto): ChatMessageVm {
    return {
      key: m.id,
      role: m.role,
      content: m.content,
      html: m.role === 'ASSISTANT'
        // 렌더러가 글 전체를 먼저 이스케이프하고 우리 태그만 붙인다 (newpla-ai-markdown)
        ? this.sanitizer.bypassSecurityTrustHtml(renderNewplaAiMarkdown(m.content))
        : undefined,
      model: m.model,
      inputTokens: m.inputTokens,
      outputTokens: m.outputTokens,
      createdAt: m.createdAt,
    };
  }

  private nextKey(): string {
    return `local-${++this.keySeq}`;
  }

  private scrollToBottom(smooth: boolean): void {
    setTimeout(() => {
      const el = this.scroller?.nativeElement;
      el?.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    });
  }

  private focusInput(): void {
    setTimeout(() => this.input?.nativeElement.focus());
  }

  private closeSidebarOnMobile(): void {
    if (window.innerWidth < 768) {
      this.sidebarOpen = false;
    }
  }
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isFileDrag(event: DragEvent): boolean {
  return !!event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files');
}

/** 확장자로 종류를 정한다. 브라우저가 알려 준 file.type은 OS마다 달라 믿지 않는다. 서버가 내용으로 다시 본다 */
function typeOf(file: File): string | null {
  const dot = file.name.lastIndexOf('.');
  return dot < 0 ? null : TYPE_BY_EXTENSION[file.name.slice(dot + 1).toLowerCase()] ?? null;
}

function sameFile(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

function revoke(pending: PendingFileVm): void {
  if (pending.previewUrl) {
    URL.revokeObjectURL(pending.previewUrl);
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes}B`;
  }
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)}${units[unit]}`;
}

function errorMessage(e: unknown): string {
  if (e instanceof NewplaAiXhrError) {
    return e.message;
  }
  if (e instanceof HttpErrorResponse) {
    const body = e.error;
    const msg = typeof body === 'string' ? body : body?.message ?? body?.msg;
    if (msg) {
      return msg;
    }
    if (e.status === 0) {
      return '서버에 연결하지 못했습니다.';
    }
  }
  return 'AI 답을 받지 못했습니다. 다시 시도하세요.';
}

/** localStorage는 막혀 있을 수 있다 (사생활 보호 모드 등) */
function readSaved(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSaved(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // 저장하지 못해도 이번 화면에서는 고른 모델을 쓴다
  }
}
